import { WprBaseViewCore } from '../view/WprBaseViewCore';
import { WprListInfo } from '../view/control/list/WprListInfo';
import { IWprConfigModeOption } from './inter/IWprConfigModeOption';
import { WprBaseViewMode } from './WprBaseViewMode';
import { WprConfigModeControl } from './WprConfigModeControl';
import { WprControRefreshMap } from '../view/control/map/WprControRefreshMap';

/**
 * ビューリストモード基本クラス
 */
export abstract class WprBaseViewListMode<T, U extends WprListInfo> extends WprBaseViewMode {
	// private 変数  ------------------------------------------------------------
	private m_ListName: string		= null;		// リスト名
	private m_ListInfo: U			= null;		// リスト情報
	private m_ListData: Array<any>	= null;		// リストデータ
	private m_CurModeList: string[]	= null;		// 現在のモードリスト
	private m_RowData: any			= null;		// 行データ
	// --------------------------------------------------------------------------

	// プロパティ  ---------------------------------------------------------------
	/** リスト名 */
	public get listName(): string 	{ return this.m_ListName; 	}
	/** リスト名 */
	public get rowData(): any	 	{ return this.m_RowData; 	}
	// --------------------------------------------------------------------------

	// コンストラクタ  -----------------------------------------------------------
	public constructor(listName: string) {
		super(listName);
		this.m_ListName = listName;
	}
	// --------------------------------------------------------------------------

	// abstract メソッド  -------------------------------------------------------
	/**
	 * リスト情報取得
	 * @param listName リスト名
	 * @returns リスト情報
	 */
	public abstract getListInfo(listName: string): U;
	// --------------------------------------------------------------------------

	// override メソッド  --------------------------------------------------------
	/**
	 * モードクリア処理
	 */
	public clearMode(): void {
		this.m_CurModeList = new Array();
	}

	/**
	 * モードチェック処理
	 * @param isClear trueの場合現在モードをクリアする(モードが変わらなくて反応する)
	 */
	public checkMode(isClear?: boolean): void {
		if (this.m_ListInfo == null)
			this.m_ListInfo = this.getListInfo(this.listName);
		if (this.m_ListInfo == null) {
			this.addErrorLog(`リスト名に誤りがあります。[${this.listName}]`);
			return;
		}
		if (this.m_ListData != null) {
			let idx =  0;
			for(const row of this.m_ListData) {
				this.m_ListInfo.setRowIndex(idx);
				this.m_RowData = row;
				this.setMode(this.m_CurModeList[idx]);
				super.checkMode(isClear);
				this.m_CurModeList[idx] = this.getMode();
				idx++;
			}
		}
	}

	/**
	 * コントロール定義を追加する
	 * @param name コントロール名
	 * @param option 定義オプション
	 */
	protected addControl(name: string, option: IWprConfigModeOption): void {
		if (this.m_ListInfo == null)
			this.m_ListInfo = this.getListInfo(this.listName);
		if (this.m_ListInfo == null) {
			// this.addErrorLog(`モード定義に対するコントロールが見つかりませんでした。[${name}]`);

			// ATODE
			// this.viewCore.viewInfo.addConfig(name, '', null);
		}
		if (this.controlMap.has(name) === true) {
			this.addErrorLog(`モード定義が重複しています。[${name}]`);
		}
		else {
			const cinfo = new WprConfigModeControl(name, option, this);
			// cinfo.setControl(this.m_ViewCore.viewInfo.getControlInfo(name), this.m_ModeList);
			cinfo.setListInfo(this.m_ListInfo, this.modeList);
			if (this.controlMap.has(name) === false)
				this.controlMap.set(name, cinfo);
			if (cinfo.isCheck === true)
				this.checkControlList.push(name);
		}
	}
	// --------------------------------------------------------------------------

	// public メソッド  ---------------------------------------------------------
	/**
	 * リストデータ設定処理
	 * @param listData リストデータ
	 */
	public setListData(listData: Array<any>): void {
		this.m_ListData = listData;
		this.m_CurModeList = new Array();
		for(const row of this.m_ListData)
			this.m_CurModeList.push('【初期モード】');
	}
	// --------------------------------------------------------------------------
}

/**
 * ビューリスト複数モード基本情報
 */
export abstract class WprBaseViewListModeInfo<T extends WprBaseViewCore, U> extends WprBaseViewListMode<U, WprListInfo> {
	// private 変数  ------------------------------------------------------------
	private m_Parent: T	= null;			// 親ビュー情報
	// --------------------------------------------------------------------------

	// プロパティ  ---------------------------------------------------------------
	/** 親ビュー情報 */
	public get view():  T	{ return this.m_Parent; }
	// --------------------------------------------------------------------------

	// コンストラクタ  -----------------------------------------------------------
	public constructor(listName: string) {
		super(listName);
	}
	// --------------------------------------------------------------------------

	// abstract メソッド  --------------------------------------------------------
	/**
	 * ビューの状態をチェックして行のモードを変更する
	 * @param curMode 現在のモード
	 * @param row 行データ
	 * @return 新しいモード（変更ない場合は現在のモード）
	 */
	public abstract onCheckListMode(curMode: string, row: U): string;
	// --------------------------------------------------------------------------

	// virtual メソッド  ---------------------------------------------------------
	/**
	 * モード変更通知
	 * @param oldMode 古いモード
	 * @param newMode 新しいモード
	 * @param row 行データ
	 */
	public onChangeListMode(oldMode: string, newMode: string, row: U): void {}
	// --------------------------------------------------------------------------

	// override メソッド  --------------------------------------------------------
	/**
	 * ビューの状態をチェックしてモードを変更する
	 * @param curMode 現在のモード
	 * @return 新しいモード（変更ない場合は現在のモード）
	 */
	public onCheckMode(curMode: string): string {
		return this.onCheckListMode(curMode, this.rowData);
	}

	/**
	 * モード変更通知
	 * @param oldMode 古いモード
	 * @param newMode 新しいモード
	 */
	public onChangeMode(oldMode: string, newMode: string): void {
		this.onChangeListMode(oldMode, newMode,  this.rowData);
	}

	/**
	 * View情報設定処理
	 * @param Binder バインダ
	 */
	public setViewCore(viewCore: WprBaseViewCore): void {
		this.m_Parent = viewCore as T;
		super.setViewCore(viewCore);
	}

	/**
	 * DI用コントロール情報マップ取得
	 * @returns DI要コントロール情報マップ
	 */
	public getDIControlMap(): WprControRefreshMap {
		return this.viewCore.getDIControlMap();
	}

	/**
	 * DI元インスタンス取得
	 * @returns DI元インスタンス
	 */
	public getDIObjectFromInstance(): any {
		return this.viewCore;
	}

	/**
	 * リスト情報取得
	 * @param listName リスト名
	 * @returns リスト情報
	 */
	public getListInfo(listName: string): WprListInfo {
		return this.viewCore.viewInfo.getListInfo(this.listName);
	}
	// --------------------------------------------------------------------------
}
